import type { DailyCardAdminItemDto } from '@lunary/api-client'
import { CardTextPreview, DAILY_TEXT_FIELDS } from '@lunary/ui-web/card-text'
import { resolveApiUrl } from '@lunary/ui-web/deck'
import { CardFace } from '../../shared/ui/card-face'
import { sameText, textOf } from '../../shared/lib/card-text'
import type { CardText } from '../../shared/lib/card-text'
import { dayMonth } from '../../shared/lib/moscow-date'
import { changedFields, usePublishConfirm } from '../../shared/ui/publish-confirm'
import expert from '../../shared/ui/expert.module.css'
import { errorText, usePublish, useSaveText } from './queries'
import styles from './daily-card.module.css'

export { DAILY_CARDS_KEY } from './queries'

function entryText(entry: DailyCardAdminItemDto): CardText {
  return textOf({ ...entry, body: entry.interpretation })
}

type Props = { entry: DailyCardAdminItemDto; readOnly: boolean; replaced: boolean; onPick: () => void; onDraw: () => void; drawing: boolean }

export function DailyCardEditor({ entry, readOnly, replaced, onPick, onDraw, drawing }: Props) {
  const stored = entryText(entry)
  const template = textOf(entry.template)
  const save = useSaveText(entry.date)
  const publish = usePublish(entry.date)
  const published = entry.status === 'published'
  const busy = save.isPending || publish.isPending
  const empty = stored.body.trim().length === 0
  const error = save.error ?? publish.error
  const custom = entry.card.number < 0
  const { ask, confirmed, dialog } = usePublishConfirm()
  const day = dayMonth(entry.date)
  const opened: (readonly [string, string])[] = entry.opened_count > 0 ? [['Уже открыли', `${entry.opened_count}, они сразу увидят новый текст`]] : []
  const onSave = async (next: CardText) => {
    if (published)
      await confirmed({
        title: 'Опубликовать изменения?',
        text: `Текст карты дня на ${day} изменится для всех пользователей.`,
        confirmLabel: 'Опубликовать',
        facts: [['Меняется', changedFields(DAILY_TEXT_FIELDS, stored, next)], ...opened],
      })
    return save.mutateAsync(next)
  }
  const onReset = async () => {
    if (
      published &&
      !(await ask({
        title: 'Вернуть текст по умолчанию?',
        text: `Текст карты дня на ${day} заменится описанием по умолчанию для «${entry.card.name}». Это изменится для всех пользователей.`,
        confirmLabel: 'Вернуть и опубликовать',
        facts: opened,
      }))
    )
      return
    save.mutate(template)
  }

  return (
    <>
      {published && !readOnly && (
        <div className={styles.banner}>
          <b>Опубликовано.</b>
          <span className={styles.hint}>Правки и замену карты сразу увидят пользователи.</span>
        </div>
      )}
      {replaced && entry.opened_count > 0 && (
        <div className={styles.banner} data-tone="bad">
          <b>Карту уже открыли: {entry.opened_count}.</b>
          <span className={styles.hint}>Они увидят новую карту. Текст заменён на описание по умолчанию для «{entry.card.name}», проверьте его.</span>
        </div>
      )}
      <div className={styles.previewWrap}>
        <CardTextPreview
          fields={DAILY_TEXT_FIELDS}
          value={stored}
          face={<CardFace src={resolveApiUrl(entry.face_url)} number={custom ? null : entry.card.number} name={entry.card.name} size="lg" />}
          eyebrow="Карта дня"
          name={entry.card.name}
          status={sameText(stored, template) ? 'default' : 'changed'}
          saving={save.isPending}
          saved={save.isSuccess}
          error={error ? errorText(error) : null}
          readOnly={readOnly}
          saveLabel={published ? 'Опубликовать…' : 'Сохранить'}
          savedLabel={published ? 'Опубликовано' : 'Сохранено'}
          onSave={onSave}
          onReset={() => void onReset()}
          resetting={save.isPending}
          footer={(editing) =>
            readOnly ? null : (
              <>
                <div className={expert.actions}>
                  {!published && (
                    <button type="button" disabled={editing || busy || empty} onClick={() => publish.mutate(stored)}>
                      {publish.isPending ? 'Публикую…' : 'Опубликовать'}
                    </button>
                  )}
                  <button type="button" className="secondary" disabled={editing || busy} onClick={onPick}>
                    Сменить карту
                  </button>
                  {!published && (
                    <button type="button" className="secondary" disabled={editing || busy || drawing} onClick={onDraw}>
                      {drawing ? 'Тяну…' : 'Случайная'}
                    </button>
                  )}
                </div>
                {empty && <p className={expert.hint}>Без трактовки опубликовать нельзя: нажмите на карандаш и напишите её.</p>}
                {!custom && <p className={expert.meaning}>{entry.card.upright_meaning}</p>}
              </>
            )
          }
        />
      </div>
      {dialog}
    </>
  )
}
